import { ListProps, Paging } from '@Core';
import { CameraType, ExtendedCoverage, IUpsertCameraProps } from '@EcamModel/controllers/ICameraHttpController';
import { IListWithECamServiceActive } from '@EcamModel/controllers/ILocationSMHttpController';
import {
    CameraServiceType,
    CameraWithInfo,
    LocationSM,
    PowerSupply,
    PurposeType,
    SolarPanelPositionType
} from '@EcamModel/model';
// import { ParkingSpace } from '@EcamModel/model/ParkingSpace';
import { CameraSIM } from '@EcamModel/model/CameraSIM';
import { PTZCamera, PTZPatrolMode } from '@EcamModel/model/PTZCamera';
import { ServiceSMType } from '@EcamModel/model/ServiceSM';
import IcDelete from '@assets/details-camera-icons/IcDelete';
import UploadImageIcon from '@assets/jsx-icon/UploadImageIcon';
import BaseCheckbox from '@components/BaseCheckbox';
import BaseIcon from '@components/BaseIcon';
import { BaseTextField } from '@components/BaseTextField';
import IOSSwitch from '@components/IOSSwitch';
import PopUpWarning from '@components/PopUpWarning';
import BreadCrumbs, { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import { DrawerContext, DrawerPage } from '@components/drawer/useDrawer';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess, pushWarning } from '@components/toast';
import {
    cameraController,
    cameraInstalledPhotoHttpController,
    locationController,
    parkingSpaceHttpController,
    ptzCameraHttpController,
    cameraSIMHttpController
} from '@controllers/index';
import styled from '@emotion/styled';
import useMedia from '@hooks/useMedia';
import usePopUp from '@hooks/usePopUp';
import useScrollToTop from '@hooks/useScrollToTop';
import { CancelOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    LinearProgress,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
    TypographyProps,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { autocompleteStyles } from '@pages/overview/smartFilterHighLight/components/BaseAutoCompleteForTypeMapping';
import { mappingSIMType } from '@pages/sim-management/utils';
import color from '@theme/Colors';
import theme from '@theme/Theme';
import moment, { Moment } from 'moment';
import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AiOutlinePlus } from 'react-icons/ai';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackdrop } from 'src/providers/BackdropProvider';
import CancelIcon from '../details/components/icon/CancelIcon';
import InformationIcon from '../details/components/icon/InformationIcon';
import SuccessIcon from '../details/components/icon/SuccessIcon';
import TextBgBackdrop from '../details/components/tabsGeozonesDetail/solar/components/TextBgBackdrop';
import { validateTypingIPAddress, validateTypingInput } from '../details/utils';
import { search } from '../list/helpers';
import myJson from './timezones.json';
import EvPermitExemptionSection from './components/EvPermitExemptionSection';
import {
    EvPermitZoneOption,
    fromCameraEvPermitZones,
    toEvPermitZoneOptions,
} from '@pages/cameras/utils/EvPermitHelpers';
export type MappingValue<T> = {
    key: T;
    label: string;
};

export type ServiceTypeLabel = {
    key: CameraServiceType;
    label: string;
};

export type PositionSolarPanelLabel = {
    key: SolarPanelPositionType;
    label: string;
};

export type CameraPurposesLabel = {
    key: PurposeType;
    label: string;
};

export const camServiceTypes: Record<CameraServiceType, ServiceTypeLabel> = {
    [CameraServiceType.Solar4G]: {
        key: CameraServiceType.Solar4G,
        label: 'Solar',
    },
    [CameraServiceType.ANPR]: {
        key: CameraServiceType.ANPR,
        label: 'ANPR',
    },
    [CameraServiceType.PTZ]: {
        key: CameraServiceType.PTZ,
        label: 'View360',
    },
    [CameraServiceType.ANPR_V2]: {
        key: CameraServiceType.ANPR_V2,
        label: 'ANPR V2',
    },
    [CameraServiceType.SP111]: {
        key: CameraServiceType.SP111,
        label: 'SP111',
    },
    [CameraServiceType.PMC8266]: {
        key: CameraServiceType.PMC8266,
        label: 'eCam TechOpsv4',
    },
};

export const powerSupplyMapping: Record<PowerSupply, MappingValue<PowerSupply>> = {
    [PowerSupply.Hybrid]: {
        key: PowerSupply.Hybrid,
        label: 'Hybrid',
    },
    [PowerSupply.Wired]: {
        key: PowerSupply.Wired,
        label: 'Wired',
    },
    [PowerSupply.Kit180Wh]: {
        key: PowerSupply.Kit180Wh,
        label: '45W/180Wh',
    },
    [PowerSupply.Kit555Wh]: {
        key: PowerSupply.Kit555Wh,
        label: '100W/555Wh',
    },
    [PowerSupply.Kit962Wh]: {
        key: PowerSupply.Kit962Wh,
        label: '100W/962Wh',
    },
    [PowerSupply.Kit1536Wh]: {
        key: PowerSupply.Kit1536Wh,
        label: '1536Wh',
    },
};

export const positionSolarPanelReadableMapping: Record<SolarPanelPositionType, PositionSolarPanelLabel> = {
    [SolarPanelPositionType.NNE]: {
        key: SolarPanelPositionType.NNE,
        label: 'NNE (North-North-East)',
    },
    [SolarPanelPositionType.ENE]: {
        key: SolarPanelPositionType.ENE,
        label: 'ENE (East-North-East)',
    },
    [SolarPanelPositionType.ESE]: {
        key: SolarPanelPositionType.ESE,
        label: 'ESE (East-South-East)',
    },
    [SolarPanelPositionType.SSE]: {
        key: SolarPanelPositionType.SSE,
        label: 'SSE (South-South-East)',
    },
    [SolarPanelPositionType.SSW]: {
        key: SolarPanelPositionType.SSW,
        label: 'SSW (South-South-West)',
    },
    [SolarPanelPositionType.WSW]: {
        key: SolarPanelPositionType.WSW,
        label: 'WSW (West-South-West)',
    },
    [SolarPanelPositionType.WNW]: {
        key: SolarPanelPositionType.WNW,
        label: 'WNW (West-North-West)',
    },
    [SolarPanelPositionType.NNW]: {
        key: SolarPanelPositionType.NNW,
        label: 'NNW (North-North-West)',
    },
};

export const cameraPurposes: Record<PurposeType, CameraPurposesLabel> = {
    [PurposeType.Decommissioned]: {
        key: PurposeType.Decommissioned,
        label: 'Decommissioned',
    },
    [PurposeType.Deleted]: {
        key: PurposeType.Deleted,
        label: 'Deleted',
    },
    [PurposeType.Live]: {
        key: PurposeType.Live,
        label: 'Live',
    },
    [PurposeType.OnHold]: {
        key: PurposeType.OnHold,
        label: 'On hold',
    },
    [PurposeType.Prospective]: {
        key: PurposeType.Prospective,
        label: 'Prospective',
    },
    [PurposeType.Testing]: {
        key: PurposeType.Testing,
        label: 'Testing',
    },
};

type FormValues = {
    deployedDate: Moment;

    cameraInstalledPhotos?: File[];

    note?: string;

    createVerification: boolean;

    enableGroupMatching: boolean;

    informationDetails: {
        name: string;
        mac: string;
        assetName: string;
        lensCapacity?: string;
        solarPanelPosition?: PositionSolarPanelLabel;
        purposes?: CameraPurposesLabel;
        powerSupply?: MappingValue<PowerSupply>;
    };

    service: {
        anprId?: string;
        serviceType: ServiceTypeLabel;
    };

    milesightsAccount: {
        userName: string;
        passWord: string;
        ip: string;
    };

    zoneAndGMT: {
        location?: any;
        zone?: any;
        // parking?: any;
        time?: TimeZone;
    };

    SIM?: Partial<CameraSIM>;
    extendedCoverage: Array<{
        location?: any;
        selectedZones: any[];
    }>;
    evPermit: {
        enabled: boolean;
        // Extra zones only; the camera's own zone is shown locked and always sent by BE
        extraZones: EvPermitZoneOption[];
        permitLengthSeconds: number | null;
    };
};

type GMT = {
    Name: string;
    Value: string;
};

interface TimeZone {
    value: string;
    utc: string;
    text: string;
}

export const RequiredTitle = (props: { title: string; required?: boolean; sx?: TypographyProps }) => {
    return (
        <Typography sx={{ lineHeight: '40px', width: 110, ...props.sx }}>
            {props.title}
            {props.required && <span style={{ color: 'red' }}>*</span>}:
        </Typography>
    );
};

export const validateDatePickerValue = (date: Date) => {
    const now = moment().startOf('date');
    const isValidDate = moment(date).isBefore(now);
    if (isValidDate) return 'Not valid date';
    return null;
};
export const validateDatePickerPastDate = (date: Date) => {
    const now = moment().startOf('date');
    const isValidDate = moment(date).isSameOrBefore(now);
    if (isValidDate) return 'Not valid date';
    return null;
};

const maxImageSize = 10 * 1024 * 1024;
const maxImageWidth = 8000;
const maxImageHeight = 4000;

export default function CameraUpsert() {
    const { id: cameraId, type } = useParams();
    const queryClient = useQueryClient();
    const { isMobileSM } = useMedia();
    const [zones, setZones] = useState<any[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [allLocations, setAllLocations] = useState<LocationSM[]>([]);
    const [listLocationSM, setListLocationSM] = useState<LocationSM[]>([]);

    const [selectedItem, setSelectedItem] = useState<string | undefined>();
    const [camera, setCamera] = useState<CameraWithInfo>({} as CameraWithInfo);
    const [loading, setLoading] = useState<boolean | undefined>(undefined);

    const { setActiveItem } = useContext(DrawerContext);

    useScrollToTop();

    const getListLocationSM = async () => {
        try {
            const res = await locationController.list({ page: 1, pageSize: 10000 });
            setListLocationSM(res?.rows ?? []);
        } catch {
            setListLocationSM([]);
        }
    };

    useEffect(() => {
        setActiveItem(DrawerPage.Cameras);
        getInactiveCameraMacs();
        getListLocationSM();
    }, []);

    const popUpWarningEditMac = usePopUp();

    const [selectedCameraType, setSelectedCameraType] = useState<number | null>(null);

    const isCameraSolar = selectedCameraType === CameraType.Solar;
    const isCameraPTZ = selectedCameraType === CameraType.PTZ;
    const isCameraSP111 = selectedCameraType === CameraType.SP111;

    const [activeStep, setActiveStep] = useState<number>(0);

    useEffect(() => {
        const cameraType = cameraId
            ? camera.VendorCameraId !== null
                ? 0
                : camera.PTZCameraId !== null
                ? 1
                : camera.SP111CameraId !== null
                ? 2
                : camera.PMC8266CameraId !== null
                ? 3
                : Number(type) || 0
            : Number(type) || 0;

        setSelectedCameraType(cameraType);
    }, [camera, cameraId, type]);

    const valuesDefault: FormValues = {
        deployedDate: moment(),
        note: '',
        createVerification: false,
        enableGroupMatching: true,
        cameraInstalledPhotos: [],
        informationDetails: {
            name: '',
            mac: '',
            assetName: '',
            lensCapacity: '',
            solarPanelPosition: undefined,
            powerSupply: undefined,
            purposes: cameraPurposes[PurposeType.Live],
        },
        service: {
            anprId: '',
            serviceType: camServiceTypes[CameraServiceType.Solar4G],
        },
        milesightsAccount: {
            userName: '',
            passWord: '',
            ip: '',
        },
        zoneAndGMT: {
            location: undefined,
            zone: undefined,
            time: undefined,
        },
        extendedCoverage: [],
        evPermit: {
            enabled: false,
            extraZones: [],
            permitLengthSeconds: null,
        },
    };

    const {
        handleSubmit,
        watch,
        reset,
        control,
        setValue,
        setError,
        clearErrors,
        formState: { isDirty, isValid, errors, dirtyFields, isSubmitting },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: valuesDefault,
    });

    const getDetailsCamera = () => {
        setLoading(true);
        cameraController
            .get(cameraId!)
            .then((res) => {
                setCamera(res);
                reset({
                    deployedDate: moment(res.DeployedDate),
                    note: res.Notes ?? '',
                    createVerification: res.IsVerificationsEnabled,
                    enableGroupMatching: Boolean(res.EnableGroupMatchingAt),
                    informationDetails: {
                        name: res.Name,
                        mac: res.Mac,
                        assetName: res.Asset,
                        lensCapacity: res.LensCapacity,
                        powerSupply: powerSupplyMapping[res?.PowerSupply!],
                        solarPanelPosition: positionSolarPanelReadableMapping[res?.SolarPanelPosition!],
                        purposes: cameraPurposes[res?.PurposeType!],
                    },
                    service: {
                        anprId: res.ANPRCameraId?.toString(),
                        serviceType: camServiceTypes[res.ServiceType],
                    },
                    milesightsAccount: {
                        userName: res.UserName,
                        passWord: res.PassWord,
                        ip: res.IP,
                    },
                    zoneAndGMT: {
                        location: res.Zone?.Location,
                        zone: res.Zone,
                        // parking: res?.ParkingSpace,
                        time: {
                            utc: res.TimeZone,
                        },
                    },
                    SIM: res?.CameraSIM,
                    extendedCoverage: res.ExtendedCoverage?.map((coverage) => ({
                        location: coverage,
                        selectedZones: coverage.Zones,
                    })),
                    evPermit: {
                        enabled: !!res.IsSendEVPermitEnabled,
                        extraZones: fromCameraEvPermitZones(res.EvPermitZones).filter((z) => z.Id !== res.ZoneId),
                        permitLengthSeconds: res.EvPermitLengthSeconds ?? null,
                    },
                });
            })
            .catch((err) => {
                pushError(err?.response?.data.message);
                navigate(`/cameras`);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (cameraId) {
            getDetailsCamera();
        }
    }, [cameraId]);

    useScrollToTop();
    const serviceTypeKey = watch('service.serviceType.key');
    const isANPR = serviceTypeKey === CameraServiceType.ANPR;
    const isANPR_v2 = serviceTypeKey === CameraServiceType.ANPR_V2;
    const isANPR_v3 = serviceTypeKey === CameraServiceType.SP111;
    const isANPR_TechOps = isANPR || isANPR_v2;

    const navigate = useNavigate();
    const backdrop = useBackdrop();

    const openBackdrop = () => {
        const message = <TextBgBackdrop text="Please wait for the camera to take a picture..." />;
        backdrop.setTrue(message);
    };
    const isDisplaySolarPanelField =
        !!watch('informationDetails.powerSupply') && watch('informationDetails.powerSupply')?.key !== PowerSupply.Wired;

    const disabled =
        !(isValid && isDirty) ||
        (isCameraSolar && isDisplaySolarPanelField && !watch().informationDetails?.solarPanelPosition);

    const fetchGMTTime = (listProps?: ListProps<GMT>) => {
        return new Promise<Paging<GMT>>((resolve, reject) => {
            setTimeout(() => {
                const paging: Paging<GMT> = {
                    page: 1,
                    pageSize: 9999,
                    rows: [],
                    total: 0,
                    totalPages: 1,
                };

                for (let i = -12; i < 14; i++) {
                    const Value = i.toString();
                    const Name = `UTC${i >= 0 ? '+' : ''}${i}`;

                    paging.rows.push({
                        Name: Name,
                        Value: Value,
                    });
                }
                // const arr = JSON.parse(myJson);

                const searchText = listProps?.search?.content;

                if (searchText) {
                    paging.rows = paging.rows.filter((l) => l.Name.toLowerCase().includes(searchText.toLowerCase()));
                }

                resolve(paging);
            }, 300);
        });
    };

    const [listMACs, setListMACs] = useState<string[]>([]);
    const [isLoadingListMacs, setIsLoadingListMacs] = useState<boolean>(true);

    const getInactiveCameraMacs = async () => {
        setIsLoadingListMacs(true);
        await cameraController
            .getListMacCameraInActive()
            .then((res) => {
                setListMACs(res);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoadingListMacs(false);
            });
    };

    const MACsAutocomplete = useStyledAutocomplete({
        list: {
            options: Object.values(listMACs),
            isFiltered(option, searchText) {
                return search(option, searchText);
            },
        },
        dependencyList: [listMACs],
        loading: isLoadingListMacs,
    });

    const locationAutocomplete = useStyledAutocomplete({
        async getList(props: IListWithECamServiceActive): Promise<Paging<LocationSM>> {
            const res = await locationController
                .listWithECamServiceActive({
                    ...props,
                    pageSize: 1000,
                })
                .finally(() => {});
            const currentLocation = watch().zoneAndGMT.location;
            const filteredLocation = res?.rows?.filter((l) => l?.Id === currentLocation?.Id);
            const firstLocation = filteredLocation[0];

            if (currentLocation) {
                setZones(firstLocation?.Zones ?? []);
            }

            if (!props.search?.content && res?.rows) {
                setAllLocations(res.rows);
            }

            return res;
        },
        listProps: {
            search: { fields: ['name'] } as any,
            ZoneService: [isANPR_TechOps ? ServiceSMType.Anpr : ServiceSMType.eCam],
        },
        dependencyList: [isANPR_TechOps],
    });

    // const pakingAutocomplete = useStyledAutocomplete({
    //     async getList(): Promise<Paging<ParkingSpace>> {
    //         if (watch().zoneAndGMT.location?.Id && watch()?.zoneAndGMT.zone?.Id && isCameraPTZ) {
    //             const res = await parkingSpaceHttpController.list({
    //                 pageSize: 100000,
    //                 LocationId: watch().zoneAndGMT.location?.Id,
    //                 ZoneIds: [watch().zoneAndGMT.zone?.Id],
    //             });
    //             return res;
    //         }

    //         return initPaging;
    //     },
    //     listProps: {
    //         search: { fields: ['name'] } as any,
    //     },
    //     dependencyList: isCameraPTZ ? [watch().zoneAndGMT?.location?.Id, watch().zoneAndGMT?.zone?.Id] : [isCameraPTZ],
    // });

    const filteredServiceTypes = Object.values(camServiceTypes).filter((service) => {
        if (isCameraSolar) {
            return (
                service.key === CameraServiceType.Solar4G ||
                service.key === CameraServiceType.ANPR ||
                service.key === CameraServiceType.ANPR_V2
            );
        }

        if (isCameraPTZ) {
            return service.key === CameraServiceType.PTZ;
        }

        if (isCameraSP111) {
            return service.key === CameraServiceType.SP111;
        }

        return service.key === CameraServiceType.PMC8266;
    });

    const serviceTypeAutocomplete = useStyledAutocomplete({
        list: {
            options: filteredServiceTypes,
            isFiltered(option, searchText) {
                return search(option.label, searchText);
            },
        },
        dependencyList: [selectedCameraType],
    });

    useEffect(() => {
        setValue('service.serviceType', filteredServiceTypes[0]);
    }, [selectedCameraType]);

    const powerSupplyAutocomplete = useStyledAutocomplete({
        list: {
            options: Object.values(powerSupplyMapping),
            isFiltered(option, searchText) {
                return search(option.label, searchText);
            },
        },
    });

    const positionSolarPanelAutocomplete = useStyledAutocomplete({
        list: {
            options: Object.values(positionSolarPanelReadableMapping),
            isFiltered(option, searchText) {
                return search(option.label, searchText);
            },
        },
    });

    const purposesAutocomplete = useStyledAutocomplete({
        list: {
            options: Object.values(cameraPurposes),
            isFiltered(option, searchText) {
                return search(option.label, searchText);
            },
        },
    });

    const onChangeLocation = (l: any) => {
        setValue('zoneAndGMT.location', l);
        setZones(l.Zones!);
    };
    const zoneAutocomplete = useStyledAutocomplete({
        list: {
            options: zones,
            isFiltered(option, searchText) {
                return option.PublicName.toLowerCase().includes(searchText.toLocaleLowerCase());
            },
        },
        dependencyList: [zones],
    });
    const gmtAutocomplete = useStyledAutocomplete<GMT, ListProps<GMT>>({
        getList(props: ListProps<GMT>) {
            return fetchGMTTime(props);
        },
        listProps: {
            search: {
                fields: ['Name'],
            } as any,
        },
    });

    const listSIMsAutocomplete = useStyledAutocomplete({
        async getList(props: ListProps<CameraSIM>): Promise<Paging<CameraSIM>> {
            const res = await cameraSIMHttpController.list(props).finally(() => {});
            if (camera?.CameraSIM && props.page == 1) {
                res.rows.unshift(camera?.CameraSIM);
            }
            return res;
        },
        listProps: {
            search: { fields: ['SerialNumber', 'NetworkProvider', 'PhoneNumber'] } as any,
            filter: {
                CameraId: null as any,
            },
        },
        dependencyList: [camera],
    });

    const handleConfirmAddCamera = async (data: FormValues, isDetails?: boolean) => {
        const primaryZoneId = Number(data.zoneAndGMT.zone?.Id);
        const _cameraData: CameraWithInfo & Pick<IUpsertCameraProps, 'EvPermitZoneIds'> = {
            Id: cameraId ? Number(cameraId) : undefined,
            Name: data.informationDetails.name.trim(),
            Mac: data.informationDetails.mac.trim(),
            TimeZone: data.zoneAndGMT.time?.utc,
            ZoneId: Number(data.zoneAndGMT.zone?.Id),
            VendorId: 1,
            DeployedDate: moment(data.deployedDate).utc(true).startOf('day').toDate(),
            Status: camera.Status,
            IP: data.milesightsAccount.ip,
            UserName: data.milesightsAccount.userName,
            PassWord: data.milesightsAccount.passWord.trim(),
            LensCapacity: data.informationDetails.lensCapacity,
            ServiceType: data.service.serviceType.key,
            ANPRCameraId: data.service.anprId ? parseInt(data.service.anprId) : null,
            Notes: data.note,
            Asset: data.informationDetails.assetName,
            PowerSupply: data.informationDetails.powerSupply?.key ?? undefined,
            SolarPanelPosition: data.informationDetails.solarPanelPosition?.key ?? undefined,
            PurposeType: data.informationDetails.purposes?.key ?? undefined,
            // ParkingSpaceId:
            //     isCameraPTZ && data.zoneAndGMT?.parking?.Id ? Number(data.zoneAndGMT.parking.Id) : undefined,

            // EnablePcnVerificationAt: camera.IsVerificationsEnabled
            //     ? data.createVerification
            //         ? camera.EnablePcnVerificationAt
            //         : (null as any)
            //     : data.createVerification
            //     ? new Date()
            //     : (null as any),

            EnablePcnVerificationAt: cameraId ? camera.EnablePcnVerificationAt : undefined,
            IsVerificationsEnabled: cameraId ? camera.IsVerificationsEnabled : false,
            EnableGroupMatchingAt: !cameraId
                ? !!data.enableGroupMatching
                    ? new Date()
                    : undefined
                : camera.EnableGroupMatchingAt,

            ZoneName: cameraId ? data.zoneAndGMT.zone?.PublicName : undefined,
            LocationName: cameraId ? data.zoneAndGMT.location.Name : undefined,

            PTZCameraId: cameraId ? camera.PTZCameraId : undefined,
            SP111CameraId: cameraId ? camera.SP111CameraId : undefined,
            VendorCameraId: cameraId ? camera.VendorCameraId : undefined,
            PTZCamera: isCameraPTZ
                ? ({
                      PatrolMode: PTZPatrolMode.FullPatrol,
                  } as PTZCamera)
                : undefined,
            ExtendedCoverage: isCameraPTZ
                ? (data.extendedCoverage.map((locationCoverage) => ({
                      LocationId: locationCoverage.location.Id,
                      ZoneIds: locationCoverage.selectedZones.map((zone) => zone.Id),
                  })) as ExtendedCoverage[] as any)
                : undefined,

            // EV permit: when Off, only the flag is sent so BE keeps zones + permit length
            IsSendEVPermitEnabled: data.evPermit.enabled,
            ...(data.evPermit.enabled && {
                EvPermitLengthSeconds: data.evPermit.permitLengthSeconds ?? undefined,
                EvPermitZoneIds: data.evPermit.extraZones.map((z) => z.Id).filter((id) => id !== primaryZoneId),
            }),
        };

        const upsertCameraController: Promise<CameraWithInfo> = isCameraSolar
            ? cameraController.upsertWithSolarCamera(_cameraData as IUpsertCameraProps)
            : isCameraPTZ
            ? cameraController.upsertWithPTZCamera(_cameraData as IUpsertCameraProps)
            : isCameraSP111
            ? cameraController.upsertWithSP111Camera(_cameraData as IUpsertCameraProps)
            : cameraController.upsertWithPMC8266Camera(_cameraData as IUpsertCameraProps);

        cameraId ? backdrop.setTrue() : openBackdrop();

        await upsertCameraController
            .then(async (res) => {
                if (!cameraId && isCameraPTZ) {
                    ptzCameraHttpController.refreshLatestPTZCameraConfig(Number(res.Id));
                }
                if (isCameraSolar || isCameraPTZ) {
                    await addCameraSIM(Number(res.Id!), data.SIM);
                }

                queryClient.invalidateQueries(['trailAuditEvent']);
                navigate(
                    `${
                        !!isDetails
                            ? `/cameras/${res.Id}`
                            : isCameraSolar
                            ? `/cameras`
                            : isCameraPTZ
                            ? `/cameras?cameraType=1`
                            : isCameraSP111
                            ? `/cameras?cameraType=2`
                            : `/cameras?cameraType=3`
                    }`
                );
                if (cameraId) {
                    pushSuccess('Saved successfully');
                } else {
                    if (res.Id && cameraInstalledPhotos.length > 0) {
                        await handleCreateCameraInstalledPhotos(res.Id);
                    }
                    if (res.CameraPhotos?.[0]) {
                        pushSuccess('Added new camera successfully');
                    } else {
                        pushWarning("Didn't receive new photos from camera. Please try again");
                    }
                }
            })
            .catch((err) => {
                if (err.response.data.statusCode === 409)
                    setError('informationDetails.mac', { message: err.response.data.message });
                if (err.response.data.statusCode === 400)
                    setError('service.anprId', { message: err.response.data.message });
            })
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const handleCreateCameraInstalledPhotos = async (cameraId: number) => {
        const formData = new FormData() as any;
        cameraInstalledPhotos.forEach((file) => {
            formData.append('CameraInstalledPhotos', file);
        });

        await cameraInstalledPhotoHttpController.createCameraInstalledPhotos(cameraId, formData);
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const [cameraInstalledPhotos, setCameraInstalledPhotos] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState({});

    const handleFileChange = (event) => {
        const newFiles = Array.from(event.target.files);

        if (cameraInstalledPhotos.length + newFiles.length > 10) {
            pushError('You can only upload a maximum of 10 images.');
            return;
        }

        newFiles.forEach((file: any) => {
            if (file.size > maxImageSize) {
                pushWarning(
                    `The maximum size can only be ${(maxImageSize / (1024 * 1024)).toFixed(
                        2
                    )}MB, you have just uploaded a file that is ${(file.size / (1024 * 1024)).toFixed(2)}MB in size.`
                );
                return;
            }

            const reader = new FileReader();
            const img = new Image();
            reader.onload = (e: any) => {
                img.onload = () => {
                    if (img.width > maxImageWidth || img.height > maxImageHeight) {
                        pushWarning(
                            `The maximum dimensions allowed are ${maxImageWidth}x${maxImageHeight}px. The uploaded file has dimensions ${img.width}x${img.height}px.`
                        );
                        return;
                    }
                    setCameraInstalledPhotos((prevFiles) => {
                        if (prevFiles.length >= 10) {
                            return prevFiles;
                        }
                        const fileExists = prevFiles.some((prevFile) => prevFile.name === file.name);
                        if (!fileExists) {
                            return [...prevFiles, file];
                        }
                        return prevFiles;
                    });
                    let progress = 0;
                    const interval = setInterval(() => {
                        progress += 25;
                        setUploadProgress((prev) => ({
                            ...prev,
                            [file.name]: progress,
                        }));
                        if (progress >= 100) {
                            clearInterval(interval);
                        }
                    }, 100);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemoveFile = (fileName: string) => {
        setCameraInstalledPhotos(cameraInstalledPhotos.filter((file) => file.name !== fileName));
        const updatedProgress = { ...uploadProgress };
        delete updatedProgress[fileName];
        setUploadProgress(updatedProgress);
    };

    const handleRadioChangeCameraType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCameraType(Number(event.target.value));
        setValue('informationDetails.solarPanelPosition', undefined);
    };

    const sectionRefs = useRef<HTMLDivElement[]>([]);

    const handleStepClick = (index: number) => {
        setActiveStep(index);
        sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const breadcrumbs: IBreadCrumbs[] = [
        {
            title: `Camera list`,
            onBack: true,
        },
        {
            title: `${cameraId ? 'Edit camera' : 'Add new camera'}`,
            isColorBlackText: true,
        },
    ];

    const evPermitZoneOptions = React.useMemo(() => toEvPermitZoneOptions(listLocationSM), [listLocationSM]);
    const selectedPrimaryZone = watch().zoneAndGMT?.zone;
    const selectedPrimaryLocation = watch().zoneAndGMT?.location;
    const evPermitPrimaryZone: EvPermitZoneOption | undefined = selectedPrimaryZone?.Id
        ? {
              Id: Number(selectedPrimaryZone.Id),
              PublicName: selectedPrimaryZone.PublicName,
              LocationId: Number(selectedPrimaryLocation?.Id ?? selectedPrimaryZone.LocationId),
              LocationName: selectedPrimaryLocation?.Name ?? '',
          }
        : undefined;

    const isEvPermitIncomplete = !!watch().evPermit?.enabled && !watch().evPermit?.permitLengthSeconds;

    const steps: BaseFormSectionProps[] = [
        {
            id: 1,
            heading: 'Information details',
            isStatusError:
                !watch().informationDetails?.name ||
                !watch().informationDetails?.mac ||
                (isCameraSolar && !watch().informationDetails?.powerSupply) ||
                (isCameraSolar && isDisplaySolarPanelField && !watch().informationDetails?.solarPanelPosition) ||
                !!errors.informationDetails,
            content: (
                <Grid container xs={12} spacing={2} item>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="informationDetails.name"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please enter a name.',
                                },
                                validate: validateTypingInput,
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="Name"
                                    required
                                    isVariantOutlined
                                    placeholder={'Camera name'}
                                    maxLength={50}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="informationDetails.mac"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please enter a mac address.',
                                },
                                validate: validateTypingInput,
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <Autocomplete
                                    value={field.value}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(val) => {
                                                if (val.target.value) {
                                                    setValue('informationDetails.mac', val.target.value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }
                                            }}
                                            label={
                                                <span>
                                                    Mac address<span style={{ color: 'red' }}>{`*`}</span>
                                                </span>
                                            }
                                            placeholder="Enter mac"
                                        />
                                    )}
                                    sx={{ ...autocompleteStyles }}
                                    onChange={(e, val) => {
                                        field.onChange(val);
                                    }}
                                    id="free-solo-demo"
                                    freeSolo
                                    options={listMACs.map((option) => option)}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="informationDetails.assetName"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="Asset"
                                    isVariantOutlined
                                    placeholder={'Asset name'}
                                    maxLength={50}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="informationDetails.lensCapacity"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="Len caps"
                                    isVariantOutlined
                                    placeholder={'Lens cap'}
                                    maxLength={30}
                                />
                            )}
                        />
                    </Grid>
                    {isCameraSolar && (
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="informationDetails.powerSupply"
                                control={control}
                                rules={{
                                    required: {
                                        value: isCameraSolar,
                                        message: 'Please select a power supply.',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...powerSupplyAutocomplete}
                                        {...field}
                                        getOptionLabel={(o) => o.label}
                                        label={
                                            <span>
                                                Power supply<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        placeholder="Please select a power supply"
                                        disabledAllOption
                                        value={field.value}
                                        onChange={field.onChange}
                                        wrapperWidth={'100%'}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={!isCameraSolar ? 12 : 6}>
                        <Controller
                            name="informationDetails.purposes"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...purposesAutocomplete}
                                    {...field}
                                    getOptionLabel={(o) => o.label}
                                    label="Purposes"
                                    value={field.value}
                                    placeholder="All purposes"
                                    disabledAllOption
                                    onChange={field.onChange}
                                    wrapperWidth={'100%'}
                                />
                            )}
                        />
                    </Grid>

                    {isCameraSolar && isDisplaySolarPanelField && (
                        <Grid item xs={12}>
                            <Controller
                                name="informationDetails.solarPanelPosition"
                                control={control}
                                rules={{
                                    required: {
                                        value: isCameraSolar || isDisplaySolarPanelField,
                                        message: 'Please select a position.',
                                    },
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <StyledAutocomplete
                                        {...positionSolarPanelAutocomplete}
                                        {...field}
                                        getOptionLabel={(o) => o.label}
                                        label={
                                            <span>
                                                Solar panel position<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        placeholder="Please select a position"
                                        disabledAllOption
                                        value={field.value}
                                        onChange={field.onChange}
                                        wrapperWidth={'100%'}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                </Grid>
            ),
        },

        {
            id: 2,
            heading: 'Service',
            isStatusError:
                !watch().service?.serviceType ||
                (isANPR_TechOps && (!watch().service?.anprId || isNaN(Number(watch().service?.anprId)))) ||
                !!errors.service,
            content: (
                <Stack spacing={2}>
                    <Controller
                        name="service.serviceType"
                        control={control}
                        // rules={{
                        //     required: {
                        //         value: true,
                        //         message: 'Please select a service type.',
                        //     },
                        // }}
                        render={({ field, fieldState: { error } }) => (
                            <StyledAutocomplete
                                {...serviceTypeAutocomplete}
                                {...field}
                                getOptionLabel={(o) => o.label}
                                label={
                                    <span>
                                        Service type<span style={{ color: 'red' }}>{`*`}</span>
                                    </span>
                                }
                                disabledAllOption
                                value={field.value}
                                onChange={(s) => {
                                    setValue('service.serviceType', s!, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                        shouldDirty: true,
                                    });
                                    if (s!.key === CameraServiceType.Solar4G) {
                                        setValue('service.anprId', undefined);
                                        setValue('service.anprId', '');
                                    }
                                    clearErrors('service.anprId');
                                    setValue('zoneAndGMT.location', undefined, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                        shouldDirty: true,
                                    });
                                    setValue('zoneAndGMT.zone', undefined, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                        shouldDirty: true,
                                    });
                                }}
                                wrapperWidth={'100%'}
                            />
                        )}
                    />
                    <Collapse in={isANPR_TechOps || isANPR_v3}>
                        <Controller
                            name="service.anprId"
                            control={control}
                            rules={{
                                required: {
                                    value: isANPR_TechOps,
                                    message: 'This field is required.',
                                },
                                validate: (value) => {
                                    if (isANPR_TechOps) {
                                        if (!value || isNaN(Number(value))) {
                                            return 'Please enter a valid ANPR ID.';
                                        }
                                    }
                                    return true;
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <BaseTextField
                                    {...field}
                                    label="ANPR ID"
                                    required={isANPR_TechOps}
                                    isVariantOutlined
                                    placeholder={'ANPR ID'}
                                    error={!!error?.message}
                                    maxLength={30}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </Collapse>
                </Stack>
            ),
        },
        {
            id: 3,
            heading: 'Milesights account',
            isStatusError: Object.values(watch().milesightsAccount).some((item) => !item) || !!errors.milesightsAccount,
            content: (
                <Stack spacing={2}>
                    <Controller
                        name="milesightsAccount.ip"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please enter an IP address.',
                            },
                            validate: validateTypingIPAddress,
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <BaseTextField
                                {...field}
                                label="IP"
                                required
                                isVariantOutlined
                                placeholder={'IP address'}
                                maxLength={30}
                                error={!!error?.message}
                                helperText={error?.message}
                            />
                        )}
                    />
                    <Controller
                        name="milesightsAccount.userName"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please enter a username.',
                            },
                            validate: validateTypingInput,
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <BaseTextField
                                {...field}
                                label="Username"
                                required
                                isVariantOutlined
                                placeholder={'Username'}
                                error={!!error?.message}
                                maxLength={30}
                                helperText={error?.message}
                            />
                        )}
                    />
                    <Controller
                        name="milesightsAccount.passWord"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please enter a password.',
                            },
                            validate: (value) => {
                                if (value.trim() === '') {
                                    return 'Invalid value.';
                                }
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <BaseTextField
                                {...field}
                                label="Password"
                                required
                                isVariantOutlined
                                placeholder={'Password'}
                                error={!!error?.message}
                                maxLength={30}
                                helperText={error?.message}
                                type={showPassword ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? (
                                                <VisibilityOff sx={{ fontSize: 20 }} />
                                            ) : (
                                                <Visibility sx={{ fontSize: 20 }} />
                                            )}
                                        </IconButton>
                                    ),
                                }}
                            />
                        )}
                    />
                </Stack>
            ),
        },
        {
            id: 4,
            heading: 'Zone & GMT',
            isStatusError:
                ['location', 'zone', 'time'].some((key) => !watch().zoneAndGMT?.[key]) || !!errors.zoneAndGMT,
            content: (
                <Stack spacing={2}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 0, sm: 2 }}
                        rowGap={2}
                        flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                    >
                        <Controller
                            name="zoneAndGMT.location"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please select a location.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...locationAutocomplete}
                                    {...field}
                                    getOptionLabel={(o) => o.Name}
                                    label={
                                        <span>
                                            Location<span style={{ color: 'red' }}>{`*`}</span>
                                        </span>
                                    }
                                    placeholder="Select location"
                                    disabled={cameraId ? !camera.IsAbleChangeZone : false}
                                    disabledAllOption
                                    value={watch().zoneAndGMT.location}
                                    onChange={(newValue) => {
                                        setValue('zoneAndGMT.location', newValue, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                        setValue('zoneAndGMT.zone', undefined, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                        onChangeLocation(newValue);
                                    }}
                                    wrapperWidth={isMobileSM ? '100%' : '50%'}
                                />
                            )}
                        />
                        <Controller
                            name="zoneAndGMT.zone"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please select a zone.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => {
                                return (
                                    <StyledAutocomplete
                                        {...zoneAutocomplete}
                                        {...field}
                                        getOptionLabel={(o) => `${o.PublicName} (ZoneID ${o.Id})`}
                                        label={
                                            <span>
                                                Zone<span style={{ color: 'red' }}>{`*`}</span>
                                            </span>
                                        }
                                        placeholder="Select zone"
                                        disabledAllOption
                                        disabled={
                                            !watch().zoneAndGMT.location || cameraId ? !camera.IsAbleChangeZone : false
                                        }
                                        value={cameraId ? watch().zoneAndGMT.zone : field.value}
                                        onChange={(z) => {
                                            setValue('zoneAndGMT.zone', z!, {
                                                shouldValidate: true,
                                                shouldTouch: true,
                                                shouldDirty: true,
                                            });
                                        }}
                                        wrapperWidth={isMobileSM ? '100%' : '50%'}
                                    />
                                );
                            }}
                        />
                    </Stack>

                    {/* <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 0, sm: 2 }}
                        rowGap={2}
                        flexWrap={{ xs: 'wrap', sm: 'inherit' }}
                    >
                    {isCameraPTZ && (
                            <Stack width={isMobileSM ? '100%' : '50%'}>
                                <Controller
                                    name="zoneAndGMT.parking"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <StyledAutocomplete
                                            {...pakingAutocomplete}
                                            {...field}
                                            getOptionLabel={(o) => o.Name}
                                            label={'Parking'}
                                            placeholder="Select parking"
                                            disabledAllOption
                                            disabled={!watch().zoneAndGMT.location?.Id || !watch().zoneAndGMT.zone?.Id}
                                            value={watch().zoneAndGMT.parking}
                                            onChange={(newValue) => {
                                                setValue('zoneAndGMT.parking', newValue, {
                                                    shouldValidate: true,
                                                    shouldTouch: true,
                                                    shouldDirty: true,
                                                });
                                            }}
                                            wrapperWidth={'100%'}
                                        />
                                    )}
                                />
                            </Stack>
                        )} */}

                    <Controller
                        name="zoneAndGMT.time"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please select a GMT time.',
                            },
                        }}
                        render={({ field, fieldState }) => {
                            return (
                                // <Stack width={isMobileSM || !isCameraPTZ ? '100%' : '50%'}>
                                <Autocomplete
                                    {...field}
                                    id="grouped-demo"
                                    value={field.value || null}
                                    options={transformedData}
                                    groupBy={(option) => option.text}
                                    getOptionLabel={(option) => option.utc}
                                    isOptionEqualToValue={(option, value) => option.utc === value.utc}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            padding: '9.95px 14px !important',
                                        },
                                        '& .MuiButtonBase-root': {
                                            marginRight: '5px !important',
                                        },
                                        '& .MuiAutocomplete-inputRoot': {
                                            '& 	.MuiAutocomplete-input': {
                                                padding: '0px 0px',
                                            },
                                        },
                                    }}
                                    renderGroup={(params) => (
                                        <li key={params.key}>
                                            <GroupHeader>
                                                <Typography variant="h6" color={(theme) => theme.palette.grey[600]}>
                                                    {params.group}
                                                </Typography>
                                            </GroupHeader>
                                            <GroupItems>{params.children}</GroupItems>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            label={
                                                <span>
                                                    Time<span style={{ color: 'red' }}>{`*`}</span>
                                                </span>
                                            }
                                            placeholder="Select time zone"
                                        />
                                    )}
                                    onChange={(e, val) => {
                                        field.onChange(val);
                                    }}
                                />
                                // </Stack>
                            );
                        }}
                    />
                    {/* </Stack> */}
                </Stack>
            ),
        },
        ...(isCameraPTZ
            ? [
                  {
                      id: 5,
                      heading: 'Extended Coverage',
                      isStatusError: false,
                      content: (
                          <Stack spacing={1}>
                              {watch().extendedCoverage?.map((coverage, index) => {
                                  const selectedLocationIds = [
                                      watch().zoneAndGMT?.location?.Id,
                                      ...watch()
                                          .extendedCoverage?.filter((_, i) => i !== index)
                                          .map((c) => c.location?.Id),
                                  ].filter(Boolean);

                                  return (
                                      <Stack
                                          key={index}
                                          spacing={1}
                                          sx={{
                                              pb: 2,
                                              borderBottom:
                                                  index < watch().extendedCoverage?.length - 1
                                                      ? `1px solid ${color.grey200}`
                                                      : 'none',
                                          }}
                                      >
                                          <Stack direction="row" alignItems="center" justifyContent={'space-between'}>
                                              <Typography variant="h6" color={(theme) => theme.palette.grey[700]}>
                                                  Location {index + 1}
                                              </Typography>
                                              <BaseIcon
                                                  isDelete
                                                  colorHover={theme.palette.error.main}
                                                  children={<IcDelete />}
                                                  onClick={() => {
                                                      const newCoverage = [...watch().extendedCoverage];
                                                      newCoverage.splice(index, 1);
                                                      setValue('extendedCoverage', newCoverage, {
                                                          shouldValidate: true,
                                                          shouldDirty: true,
                                                      });
                                                  }}
                                              />
                                          </Stack>

                                          <Autocomplete
                                              value={coverage.location || null}
                                              options={allLocations.filter(
                                                  (loc) => !selectedLocationIds.includes(loc.Id)
                                              )}
                                              getOptionLabel={(o) => o.Name || ''}
                                              isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                                              onChange={(e, newValue) => {
                                                  const newCoverage = [...watch().extendedCoverage];
                                                  const firstZone = newValue?.Zones?.[0] || null;
                                                  newCoverage[index] = {
                                                      location: newValue,
                                                      selectedZones: firstZone ? [firstZone] : [],
                                                  };
                                                  setValue('extendedCoverage', newCoverage, {
                                                      shouldValidate: true,
                                                      shouldDirty: true,
                                                  });
                                              }}
                                              renderInput={(params) => (
                                                  <TextField
                                                      {...params}
                                                      InputLabelProps={{ shrink: true }}
                                                      label="Location"
                                                      placeholder="Select location"
                                                  />
                                              )}
                                              clearIcon={false}
                                              sx={{
                                                  ...autocompleteStyles,
                                                  '& .MuiAutocomplete-endAdornment': {
                                                      top: '50%',
                                                  },
                                              }}
                                          />

                                          {coverage.location &&
                                              (() => {
                                                  const fullLocationData = allLocations.find(
                                                      (loc) => loc.Id === coverage.location.Id
                                                  );
                                                  const zonesToDisplay = fullLocationData?.Zones || [];

                                                  return (
                                                      <Stack direction="column" spacing={1}>
                                                          <Typography
                                                              variant="body2"
                                                              color={(theme) => theme.palette.grey[600]}
                                                          >
                                                              Zones in {coverage.location.Name}
                                                          </Typography>
                                                          <Stack bgcolor={color.grey100} borderRadius={1} p={1} px={2}>
                                                              <FormGroup sx={{ flexDirection: 'column', gap: 1 }}>
                                                                  {zonesToDisplay?.map((zone: any) => (
                                                                      <FormControlLabel
                                                                          key={zone.Id}
                                                                          control={
                                                                              <BaseCheckbox
                                                                                  checked={coverage.selectedZones.some(
                                                                                      (z) => z.Id === zone.Id
                                                                                  )}
                                                                                  onChange={(e) => {
                                                                                      const newCoverage = [
                                                                                          ...watch().extendedCoverage,
                                                                                      ];
                                                                                      if (e.target.checked) {
                                                                                          newCoverage[
                                                                                              index
                                                                                          ].selectedZones = [
                                                                                              ...newCoverage[index]
                                                                                                  .selectedZones,
                                                                                              zone,
                                                                                          ];
                                                                                      } else {
                                                                                          newCoverage[
                                                                                              index
                                                                                          ].selectedZones = newCoverage[
                                                                                              index
                                                                                          ].selectedZones.filter(
                                                                                              (z) => z.Id !== zone.Id
                                                                                          );
                                                                                      }
                                                                                      setValue(
                                                                                          'extendedCoverage',
                                                                                          newCoverage,
                                                                                          {
                                                                                              shouldValidate: true,
                                                                                              shouldDirty: true,
                                                                                          }
                                                                                      );
                                                                                  }}
                                                                              />
                                                                          }
                                                                          label={
                                                                              <Typography ml={0.5} mt={0.3}>
                                                                                  {zone.PublicName}
                                                                              </Typography>
                                                                          }
                                                                      />
                                                                  ))}
                                                              </FormGroup>
                                                          </Stack>
                                                      </Stack>
                                                  );
                                              })()}
                                      </Stack>
                                  );
                              })}

                              <Button
                                  startIcon={<AiOutlinePlus size={14} />}
                                  variant="outlinedGreen"
                                  sx={{
                                      alignSelf: 'flex-start',
                                      textTransform: 'none',
                                  }}
                                  onClick={() => {
                                      const selectedLocationIds = [
                                          watch().zoneAndGMT?.location?.Id,
                                          ...watch().extendedCoverage?.map((c) => c.location?.Id),
                                      ].filter(Boolean);

                                      const availableLocations = allLocations.filter(
                                          (loc) => !selectedLocationIds.includes(loc.Id)
                                      );

                                      if (availableLocations.length > 0) {
                                          const firstLocation = availableLocations[0];
                                          const firstZone = firstLocation.Zones?.[0] || null;

                                          setValue(
                                              'extendedCoverage',
                                              [
                                                  ...watch().extendedCoverage,
                                                  {
                                                      location: firstLocation,
                                                      selectedZones: firstZone ? [firstZone] : [],
                                                  },
                                              ],
                                              {
                                                  shouldValidate: true,
                                                  shouldDirty: true,
                                              }
                                          );
                                      } else {
                                          pushWarning('No more locations available to add.');
                                      }
                                  }}
                              >
                                  Add coverage from another location
                              </Button>
                          </Stack>
                      ),
                  },
              ]
            : []),
        {
            id: 6,
            heading: 'Production',
            isStatusError: !!errors.deployedDate && !cameraId,
            content: (
                <Stack spacing={2}>
                    <Controller
                        name="deployedDate"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please choose a date.',
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <DesktopDatePicker
                                {...field}
                                label={
                                    <span>
                                        Production<span style={{ color: 'red' }}>{`*`}</span>
                                    </span>
                                }
                                value={field.value}
                                format="DD/MM/YYYY"
                                shouldDisableDate={(date) =>
                                    cameraId ? false : validateDatePickerValue(moment(date).toDate()) !== null
                                }
                                slotProps={{
                                    textField: {
                                        inputProps: {
                                            readOnly: true,
                                        },
                                        fullWidth: true,
                                    },
                                }}
                            />
                        )}
                    />
                </Stack>
            ),
        },

        {
            id: 7,
            heading: isMobileSM ? 'Photo as installed' : 'Photo of camera as installed',
            isStatusError: !!errors.cameraInstalledPhotos,
            content: (
                <Stack spacing={2}>
                    <Stack alignItems="center" spacing={2}>
                        <Controller
                            name="cameraInstalledPhotos"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <Stack
                                        sx={{
                                            border: '1px dashed #ccc',
                                            borderRadius: '5px',
                                            width: '100%',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            mt: 2,
                                        }}
                                        flex={1}
                                        justifyContent={'center'}
                                    >
                                        <input
                                            type="file"
                                            multiple
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            accept=".svg,.png,.jpg,.jpeg,.gif,.webp,.ico"
                                            id="file-upload"
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            style={{ padding: isMobileSM ? '4px 0px' : '16px' }}
                                        >
                                            <UploadImageIcon />
                                            <Typography sx={{ cursor: 'pointer' }}>Click to upload</Typography>
                                            <Typography
                                                sx={{ cursor: 'pointer' }}
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                SVG, PNG, JPG or GIF (max. {maxImageWidth}x{maxImageHeight}px)
                                            </Typography>
                                        </label>
                                    </Stack>

                                    {cameraInstalledPhotos.map((file, index) => (
                                        <Box
                                            key={index}
                                            display="flex"
                                            alignItems="center"
                                            width={'100%'}
                                            border={`1px solid ${color.grey200}`}
                                            borderRadius={'5px'}
                                            p={{ xs: '4px 8px', sm: '8px 16px' }}
                                        >
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt=""
                                                width={'40px'}
                                                height={'40px'}
                                                style={{
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => {
                                                    setSelectedItem(URL.createObjectURL(file));
                                                }}
                                            />
                                            <Stack flex={1} ml={3}>
                                                <Typography>{file.name}</Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={uploadProgress[file.name] || 0}
                                                    sx={{
                                                        height: 5,
                                                        borderRadius: 5,
                                                        backgroundColor: color.grey300,
                                                        width: '98%',
                                                    }}
                                                />
                                                <Typography
                                                    display={{ xs: 'none', sm: 'inherit' }}
                                                    variant="caption"
                                                    color={'rgba(147, 213, 0, 1)'}
                                                >
                                                    {uploadProgress[file.name] < 100
                                                        ? `${uploadProgress[file.name]}% uploaded`
                                                        : 'Uploaded successfully!'}
                                                </Typography>
                                            </Stack>

                                            <IconButton
                                                color="secondary"
                                                onClick={() => handleRemoveFile(file.name)}
                                                disabled={uploadProgress[file.name] < 100}
                                            >
                                                <CancelOutlined
                                                    sx={{
                                                        color: `${uploadProgress[file.name] < 100 ? 'red' : 'gray'}`,
                                                        width: 16,
                                                        height: 16,
                                                    }}
                                                />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    {selectedItem && (
                                        <Dialog
                                            open={true}
                                            onClose={() => {
                                                setSelectedItem(undefined);
                                            }}
                                            PaperProps={{
                                                sx: {
                                                    minWidth: { xs: 300, sm: 580, xl: 1280 },
                                                    minHeight: { xs: 168.75, sm: 326.25, xl: 720 },
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    aspectRatio: '16/9',
                                                    background: `url(${selectedItem}) no-repeat`,
                                                    backgroundPosition: 'center',
                                                    backgroundSize: 'contain',
                                                    backgroundRepeat: 'no-repeat',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </Dialog>
                                    )}
                                </>
                            )}
                        />
                    </Stack>
                </Stack>
            ),
        },

        {
            id: 8,
            heading: 'Note',
            isStatusError: !!errors.note,
            content: (
                <Stack spacing={2}>
                    <Controller
                        name="note"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <BaseTextField
                                {...field}
                                label={'Note'}
                                isVariantOutlined
                                placeholder={'Enter a description'}
                                maxLength={255}
                            />
                        )}
                    />
                </Stack>
            ),
        },
        {
            id: 9,
            heading: 'Data processing',
            isStatusError: !!errors.createVerification && !!errors.enableGroupMatching,
            isHorizontalContent: isMobileSM ? false : true,
            content: (
                <Stack direction="row" spacing={2}>
                    {cameraId && (
                        <>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <RequiredTitle title="Create verifications" sx={{ width: 150 }} />
                                <Controller
                                    name="createVerification"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <IOSSwitch
                                            {...field}
                                            label={
                                                <Typography sx={{ width: 'max-content' }}>
                                                    {!!field.value ? 'On' : 'Off'}
                                                </Typography>
                                            }
                                            iosSwitchProps={{
                                                checked: field.value,
                                                onChange(event, checked) {
                                                    field.onChange(event);
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                            <Divider orientation="vertical" variant="middle" flexItem />
                        </>
                    )}
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <RequiredTitle title="Enable group matching" sx={{ width: 175 }} />
                        <Controller
                            name="enableGroupMatching"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <IOSSwitch
                                    {...field}
                                    label={
                                        <Typography sx={{ width: 'max-content' }}>
                                            {!!field.value ? 'On' : 'Off'}
                                        </Typography>
                                    }
                                    iosSwitchProps={{
                                        checked: field.value,
                                        onChange(event, checked) {
                                            field.onChange(event);
                                        },
                                    }}
                                />
                            )}
                        />
                    </Stack>
                </Stack>
            ),
        },
        {
            id: 11,
            heading: 'EV permit exemption',
            isStatusError: isEvPermitIncomplete,
            headerAction: (
                <Controller
                    name="evPermit.enabled"
                    control={control}
                    render={({ field }) => (
                        <IOSSwitch
                            label={<Typography sx={{ width: 'max-content' }}>{field.value ? 'On' : 'Off'}</Typography>}
                            iosSwitchProps={{
                                checked: !!field.value,
                                onChange(_, checked) {
                                    field.onChange(checked);
                                },
                            }}
                        />
                    )}
                />
            ),
            content: (
                <EvPermitExemptionSection
                    enabled={!!watch().evPermit?.enabled}
                    primaryZone={evPermitPrimaryZone}
                    extraZones={watch().evPermit?.extraZones ?? []}
                    onChangeExtraZones={(zones) =>
                        setValue('evPermit.extraZones', zones, { shouldDirty: true, shouldValidate: true })
                    }
                    permitLengthSeconds={watch().evPermit?.permitLengthSeconds ?? null}
                    onChangePermitLength={(seconds) =>
                        setValue('evPermit.permitLengthSeconds', seconds, { shouldDirty: true, shouldValidate: true })
                    }
                    zoneOptions={evPermitZoneOptions}
                />
            ),
        },
        ...(isCameraSolar || isCameraPTZ
            ? [
                  {
                      id: 10,
                      heading: 'SIM',
                      isStatusError: false,
                      content: (
                          <Stack spacing={2}>
                              <Controller
                                  name={'SIM'}
                                  control={control}
                                  render={({ field }) => (
                                      <Autocomplete
                                          {...field}
                                          options={listSIMsAutocomplete?.paging?.rows || []}
                                          value={field.value}
                                          getOptionLabel={(option) =>
                                              `${option.NetworkProvider}${
                                                  option.SerialNumber ? ` - ${option.SerialNumber}` : ''
                                              } - ${mappingSIMType[option.Type ?? -1]}`
                                          }
                                          renderInput={(params) => (
                                              <TextField
                                                  {...params}
                                                  InputLabelProps={{
                                                      shrink: true,
                                                  }}
                                                  label={'SIM'}
                                                  placeholder="Select SIM"
                                              />
                                          )}
                                          sx={{ ...autocompleteStyles }}
                                          onChange={(_, val) => {
                                              field.onChange(val);
                                          }}
                                          isOptionEqualToValue={(option, value) => option.Id === value.Id}
                                      />
                                  )}
                              />
                          </Stack>
                      ),
                  },
              ]
            : []),
    ];

    const excludeSteps = [7, 9];
    const filteredSteps = cameraId ? steps.filter((step) => !excludeSteps.includes(step.id)) : steps;
    const hasErrorInFilteredSteps = filteredSteps.some((step) => step.isStatusError);

    const addCameraSIM = async (camId: number, SIM?: Partial<CameraSIM>) => {
        if (!SIM) {
            if (cameraId && camera.CameraSIM?.Id)
                await cameraSIMHttpController.upsert({ Id: camera.CameraSIM?.Id, CameraId: null as any });

            return;
        }

        if (SIM.Id === camera.CameraSIM?.Id) return;

        if (camera.CameraSIM?.Id)
            await cameraSIMHttpController.upsert({ Id: camera.CameraSIM?.Id, CameraId: null as any });

        await cameraSIMHttpController.upsert({ Id: SIM.Id, CameraId: camId });
    };
    const hasExtendedCoverageWithoutZones = watch().extendedCoverage?.some(
        (coverage) => coverage.location && coverage.selectedZones.length === 0
    );

    return (
        <Box>
            {loading ? (
                <Stack justifyContent="center" alignItems="center" minHeight={600}>
                    <CircularProgress />
                </Stack>
            ) : (
                <Box>
                    {/* Title add new camera */}
                    <Stack direction={'column'} spacing={1}>
                        <Typography variant="h3">{cameraId ? 'Edit camera' : 'Add new camera'}</Typography>
                        <BreadCrumbs breadcrumbs={breadcrumbs} />
                    </Stack>

                    {/* Content body */}
                    <Stack
                        bgcolor={color.grey100}
                        borderRadius={1}
                        p={2}
                        direction={'column'}
                        spacing={cameraId ? 0 : 2}
                        mt={3}
                    >
                        {/* Choose camera type */}
                        <Grid container xs={12} alignItems="center">
                            {cameraId ? (
                                <Typography variant="h5" mb={2}>
                                    Type:
                                    {camera.PTZCameraId !== null
                                        ? 'Camera View 360'
                                        : camera.SP111CameraId !== null
                                        ? 'Camera SP111'
                                        : camera.PMC8266CameraId !== null
                                        ? 'Camera PMC8266'
                                        : 'Camera Solar'}
                                </Typography>
                            ) : (
                                <>
                                    <Grid xs={0} sm={6} lg={3} display={{ xs: 'none', sm: 'inherit' }}>
                                        <Typography variant="h5">Select camera type to add:</Typography>
                                    </Grid>
                                    <Grid
                                        xs={12}
                                        sm={6}
                                        lg={9}
                                        sx={{
                                            textAlign: 'start',
                                        }}
                                    >
                                        <FormControl>
                                            <RadioGroup
                                                row
                                                value={selectedCameraType}
                                                onChange={handleRadioChangeCameraType}
                                            >
                                                {camera.VendorCameraId !== null && (
                                                    <FormControlLabel
                                                        sx={{
                                                            mr: 2,
                                                            '.MuiFormControlLabel-label': {
                                                                mt: '3px !important',
                                                            },
                                                        }}
                                                        control={<Radio value={0} />}
                                                        label="Camera Solar"
                                                    />
                                                )}
                                                {camera.PTZCameraId !== null && (
                                                    <FormControlLabel
                                                        sx={{
                                                            '.MuiFormControlLabel-label': {
                                                                mt: '3px !important',
                                                            },
                                                        }}
                                                        control={<Radio value={1} />}
                                                        label="Camera View360"
                                                    />
                                                )}
                                                {camera.SP111CameraId !== null && (
                                                    <FormControlLabel
                                                        sx={{
                                                            '.MuiFormControlLabel-label': {
                                                                mt: '3px !important',
                                                            },
                                                        }}
                                                        control={<Radio value={2} />}
                                                        label="Camera SP111"
                                                    />
                                                )}
                                                {camera.PMC8266CameraId !== null && (
                                                    <FormControlLabel
                                                        sx={{
                                                            '.MuiFormControlLabel-label': {
                                                                mt: '3px !important',
                                                            },
                                                        }}
                                                        control={<Radio value={3} />}
                                                        label="Camera PMC8266"
                                                    />
                                                )}
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        <Grid container xs={12} columnSpacing={{ xs: 0, md: 2 }} margin={'0 auto'}>
                            <Grid
                                item
                                xs={0}
                                md={3}
                                columnSpacing={2}
                                p={2}
                                bgcolor={color.white}
                                borderRadius={1}
                                border={`1px solid ${color.grey300}`}
                                sx={{
                                    maxHeight: 600,
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                    '::-webkit-scrollbar': {
                                        width: '2px',
                                    },
                                }}
                                display={{ xs: 'none', md: 'inherit' }}
                            >
                                <Box sx={{ flexDirection: 'column', display: 'flex', width: '100%', gap: 1 }}>
                                    {filteredSteps.map((item, index) => {
                                        const isActive = index === activeStep;
                                        return (
                                            <Button
                                                key={item.id}
                                                onClick={() => handleStepClick(index)}
                                                sx={{
                                                    width: '100%',
                                                    backgroundColor: isActive ? '#E5F5ED' : color.white,
                                                    padding: '12px 16px',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    textAlign: 'start',
                                                    fontSize: '14px',
                                                    color: isActive ? `${color.success} !important` : color.textPrimary,
                                                    justifyContent: 'space-between',
                                                }}
                                                endIcon={item.isStatusError ? <CancelIcon /> : <SuccessIcon />}
                                            >
                                                {item.heading}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={9}
                                container
                                gap={2}
                                sx={{
                                    maxHeight: 600,
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                    '::-webkit-scrollbar': {
                                        width: '2px',
                                    },
                                }}
                            >
                                {filteredSteps.map((step, index) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            key={step.id}
                                            ref={(el) => {
                                                if (el) {
                                                    sectionRefs.current[index] = el;
                                                }
                                            }}
                                        >
                                            <BaseFormSection
                                                id={step.id}
                                                isStatusError={step.isStatusError}
                                                heading={step.heading}
                                                content={step.content}
                                                isHorizontalContent={step.isHorizontalContent}
                                                isAbleChangeZone={cameraId ? camera.IsAbleChangeZone : true}
                                                isMobile={isMobileSM}
                                                headerAction={step.headerAction}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </Stack>

                    <Box
                        position={{ xs: 'fixed', md: 'inherit' }}
                        bottom={0}
                        zIndex={100}
                        width={'100%'}
                        bgcolor={color.white}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mt={{ xs: 0, md: 4 }}
                            py={2}
                        >
                            <Button
                                variant="cancel"
                                sx={{ flexBasis: { xs: 0, sm: 175 } }}
                                onClick={() => {
                                    navigate(-1);
                                }}
                            >
                                Cancel
                            </Button>
                            <Stack
                                direction="row"
                                spacing={{ xs: '4px', md: 3 }}
                                width={{ xs: '60%', md: '50%' }}
                                justifyContent="flex-end"
                                position={{ xs: 'fixed', md: 'inherit' }}
                                right={30}
                            >
                                {!cameraId && (
                                    <Button
                                        variant="outlined"
                                        sx={{
                                            flexBasis: 185,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                        }}
                                        onClick={handleSubmit((data) => handleConfirmAddCamera(data, true))}
                                        disabled={disabled || hasExtendedCoverageWithoutZones || isEvPermitIncomplete}
                                    >
                                        Save & add geo zone
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    sx={{ flexBasis: { xs: 0, sm: 175 } }}
                                    onClick={handleSubmit((data) => {
                                        if (cameraId) {
                                            if (
                                                dirtyFields.informationDetails?.mac &&
                                                !isSubmitting &&
                                                !errors.informationDetails?.mac &&
                                                isCameraSolar
                                            ) {
                                                popUpWarningEditMac.setTrue();
                                            } else {
                                                handleConfirmAddCamera(data, true);
                                            }
                                        } else {
                                            handleConfirmAddCamera(data);
                                        }
                                    })}
                                    disabled={disabled || hasErrorInFilteredSteps || hasExtendedCoverageWithoutZones}
                                >
                                    {cameraId ? 'Confirm' : 'Save'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            )}
            <PopUpWarning
                {...popUpWarningEditMac}
                title="Confirm"
                message="This action will switch the camera to the 'Not available' state until the system retrieves the latest information from this MAC. Are you sure you want to edit this MAC?"
                onConfirm={handleSubmit((data) => {
                    if (dirtyFields.informationDetails?.mac && !isSubmitting) {
                        handleConfirmAddCamera(data, true);
                        popUpWarningEditMac.onClose();
                    }
                })}
            />
        </Box>
    );
}

export const transformedData: TimeZone[] = myJson.flatMap((item) => {
    const _transformedData: TimeZone[] = [];
    item.utc.map((utc) => {
        _transformedData.push({ text: item.text, value: item.value, utc });
    });
    return _transformedData;
});

export const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    // padding: '9px 10px',
    paddingLeft: '9px',
    paddingTop: '9px',
    paddingBottom: '5px',
    color: color.grey600,
    backgroundColor: '#FFF',
}));

export const GroupItems = styled('ul')({
    padding: 0,
});

interface BaseFormSectionProps {
    id: number;
    heading: string;
    isStatusError: boolean;
    content: ReactNode;
    isHorizontalContent?: boolean;
    isAbleChangeZone?: boolean;
    isMobile?: boolean;
    /** Optional control rendered at the right of the heading (e.g. an on/off switch) */
    headerAction?: ReactNode;
}

const BaseFormSection = (props: BaseFormSectionProps) => {
    const { heading, isStatusError, content, isHorizontalContent, isAbleChangeZone, isMobile, headerAction } = props;

    const tooltipConfig: { [key: number]: { message: string; condition: boolean } } = {
        [4]: {
            message: 'This location/zone cannot currently be edited as some PCNs and images have not been processed.',
            condition: !isAbleChangeZone,
        },
    };

    return (
        <Stack p={2} direction={'column'} bgcolor={color.white} borderRadius={1} border={`1px solid ${color.grey300}`}>
            <Stack
                width={'100%'}
                justifyContent={isHorizontalContent ? 'space-between' : ''}
                direction={isHorizontalContent ? 'row' : 'column'}
            >
                <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
                    <Stack direction={'row'} spacing={1} alignItems={'center'} mb={isHorizontalContent ? 0 : 2}>
                        <Typography variant="h6" fontSize={16}>
                            {heading}
                        </Typography>
                        {isMobile ? (
                            isStatusError ? (
                                <CancelIcon />
                            ) : (
                                <SuccessIcon />
                            )
                        ) : (
                            <Typography variant="h6" color={isStatusError ? color.darkDanger : color.success}>
                                {isStatusError ? '(Not completed)' : '(Completed)'}
                            </Typography>
                        )}
                    </Stack>
                    {tooltipConfig[props.id] && tooltipConfig[props.id].condition && (
                        <Tooltip placement="top" title={tooltipConfig[props.id].message}>
                            <InformationIcon color={color.danger} />
                        </Tooltip>
                    )}
                    {headerAction && <Box mb={isHorizontalContent ? 0 : 2}>{headerAction}</Box>}
                </Stack>

                <Stack>{content}</Stack>
            </Stack>
        </Stack>
    );
};
